import { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../contexts/AuthContext";
import apiClient from "../../api/axios";
import { auth } from "../../firebase";
import { signInWithEmailAndPassword } from "firebase/auth";

export const useLoginPage = () => {
  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [password, setPassword] = useState("");
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [error, setError] = useState("");

  // パスワードリセット用state
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetCompanyName, setResetCompanyName] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const { isAuthenticated, login, adminLogin, isAdmin } =
    useContext(AuthContext)!;
  const navigate = useNavigate();

  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  useEffect(() => {
    if (isAuthenticated) {
      if (isAdmin) {
        navigate("/admin/dashboard", { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    }
  }, [isAuthenticated, isAdmin, navigate]);

  const toggleShowLoginPassword = () => {
    setShowLoginPassword(!showLoginPassword);
  };

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetMessage("");

    if (!resetEmail || !resetCompanyName) {
      setResetMessage("メールアドレスと企業名を入力してください。");
      return;
    }

    setIsSubmitting(true);
    try {
      // バックエンドのンドポイントは変更なし
      await apiClient.post("/api/auth/request-password-reset", {
        email: resetEmail,
        company_name: resetCompanyName,
      });
      setResetMessage(
        "パスワード再設定用のURLをご登録のメールアドレスへ送信しました。\nメールが届かない場合は入力をご確認のうえ、再度お試しください。"
      );
      setCooldown(30);
    } catch (err) {
      setResetMessage(
        "処理中にエラーが発生しました。時間をおいて再度お試しください。"
      );
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoggingIn(true);

    try {
      // 1. まず管理者ログインを試行
      const adminResponse = await apiClient.post("/api/auth/admin-login", {
        email,
        company_name: companyName,
        password,
      });

      // 管理者ログイン成功
      const { access_token: admin_token, company_id: admin_company_id } =
        adminResponse.data;
      adminLogin(admin_token, admin_company_id);
      navigate("/admin/dashboard"); // 管理者ダッシュボードへ
    } catch (adminError: any) {
      // 管理者ログインが失敗した場合、通常のユーザーログインを試行
      // 401 (認証エラー) or 500 (設定なしエラー) の場合にフォールバック
      if (
        adminError.response &&
        (adminError.response.status === 401 ||
          adminError.response.status === 500)
      ) {
        try {
          // 2. 通常のFirebaseログインフロー
          const userCredential = await signInWithEmailAndPassword(
            auth,
            email,
            password
          );
          const firebaseUser = userCredential.user;
          const idToken = await firebaseUser.getIdToken();

          const response = await apiClient.post(
            "/api/auth/token-login",
            { company_name: companyName },
            { headers: { Authorization: `Bearer ${idToken}` } }
          );

          const { access_token, company_id } = response.data;
          login(access_token, company_id);
          navigate("/"); // 通常のダッシュボードへ
        } catch (userError: any) {
          console.error("User login failed:", userError);
          if (
            userError.code &&
            (userError.code === "auth/user-not-found" ||
              userError.code === "auth/wrong-password" ||
              userError.code === "auth/invalid-credential")
          ) {
            setError("ログインに失敗しました。\n入力内容を確認してください。");
          } else {
            setError("ログインに失敗しました。\n入力内容を確認してください。");
          }
        }
      } else {
        // 管理者ログイン試行で401/500以外のエラーが発生した場合
        console.error("Admin login failed:", adminError);
        setError("ログイン処理中に予期せぬエラーが発生しました。");
      }
    } finally {
      setIsLoggingIn(false);
    }
  };

  return {
    email,
    setEmail,
    companyName,
    setCompanyName,
    password,
    setPassword,
    showLoginPassword,
    error,
    isResetModalOpen,
    setIsResetModalOpen,
    resetEmail,
    setResetEmail,
    resetCompanyName,
    setResetCompanyName,
    resetMessage,
    isSubmitting,
    cooldown,
    isLoggingIn,
    toggleShowLoginPassword,
    handleRequestReset,
    handleSubmit,
  };
};